import React, { useEffect, useMemo, useRef } from "react";
import { payloadToUrl, payloadToUrlForBanks } from "services";
import LazyComponent from "../../../hoc/LazyComponent";
import { AuctionPropertyListPageSidebarFilterMenu } from "./AuctionPropertyListPageSidebarFilterMenu";
import { AuthModalWrapper } from "../Auth/AuthModal";
import { PDAcknowledgmentModal } from "../propertyDetail/v2Components/PDAcknowledgmentModal";
import { PropertyListViewWrapper } from "./shared/PropertyListViewWrapper";
import { useAuctionPLSearch } from "./hooks/useAuctionPLSearch";
import { useInfiniteScroll } from "./hooks/useInfiniteScroll";
import { useRequestImages } from "./hooks/useRequestImages";
import { REQUEST_TYPES } from "./constants/auction";
import { PropertyListHeaderView, PropertyListPageWrapper } from "./shared";
import { usePLCommonStates } from "./hooks/usePLCommonStates";
import { OptionType } from "../postYourProperty/listingPageV2";
import { PropertyDataInterface } from "./shared/PropertyCardGridView";

interface AuctionPropertyListPageWrapperProps {
  cities: any;
  defaultLocation: any;
  isUrlToPayloadPrepared: boolean;
}

const bankName = process.env.NEXT_PUBLIC_BANK;

export const AuctionPropertyListPageWrapper: React.FC<
  AuctionPropertyListPageWrapperProps
> = ({ defaultLocation, cities, isUrlToPayloadPrepared }) => {
  const {
    router,
    isFilterOpen,
    setIsFilterOpen,
    isLoggedIn,
    authModalOpen,
    setAuthModalOpen,
    acknowledgementOpen,
    setAcknowledgementOpen,
    acknowledgmentErrorMessage,
    setAcknowledgmentErrorMessage,
    city,
    searchForm,
    withAuth,
    user,
  } = usePLCommonStates({ defaultLocation, cities });

  const hasCascadedRef = useRef(false);

  useEffect(() => {
    if (isUrlToPayloadPrepared) {
      if (bankName) {
        payloadToUrlForBanks(router, searchForm.value);
      } else {
        payloadToUrl(router, searchForm.value);
      }
    }
  }, [
    isUrlToPayloadPrepared,
    searchForm?.value?.asset_type?.value,
    JSON.stringify(searchForm?.value?.sub_asset_type),
    searchForm?.value?.state?.value,
    searchForm?.value?.institutes,
    searchForm?.value?.city?.value,
    searchForm?.value?.locality?.length,
    searchForm?.value?.reserve_price,
    searchForm?.value?.auction_start_date_unix_timestamp,
    searchForm?.value?.filter_by?.value,
    searchForm?.value?.sort_by?.value,
    searchForm?.value?.listing_id_query,
    searchForm?.value?.ab_verified,
    bankName,
  ]);

  const { data, loading, noMore, fetchFirstPage, fetchNextPage } =
    useAuctionPLSearch({
      router,
      searchForm,
    });

  useInfiniteScroll({
    disabled: loading || noMore,
    onReachBottom: fetchNextPage,
  });

  useEffect(() => {
    if (router.isReady) {
      if (isUrlToPayloadPrepared && !hasCascadedRef.current) {
        hasCascadedRef.current = true;
        (async () => {
          const found = await fetchFirstPage();

          if (found === 0) {
            let cascadeFound = found;
            let clearedSubAssetType = false;
            let clearedAssetType = false;

            const hasSubAssetType =
              searchForm.value.sub_asset_type &&
              searchForm.value.sub_asset_type !== "";
            if (hasSubAssetType) {
              searchForm.value.sub_asset_type = "";
              clearedSubAssetType = true;
              cascadeFound = await fetchFirstPage();
            }

            const hasAssetType =
              searchForm.value.asset_type &&
              searchForm.value.asset_type !== "" &&
              searchForm.value.asset_type?.value;
            if (cascadeFound === 0 && hasAssetType) {
              searchForm.value.asset_type = "";
              clearedAssetType = true;
              await fetchFirstPage();
            }

            if (clearedSubAssetType) {
              searchForm.setField("sub_asset_type", "");
            }
            if (clearedAssetType) {
              searchForm.setField("asset_type", "");
            }
          }
        })();
      } else {
        fetchFirstPage();
      }
    }
  }, [
    router.isReady,
    searchForm?.value?.locality?.length,
    searchForm?.value?.city?.value,
    isUrlToPayloadPrepared,
  ]);

  const handleClearFilters = () => {
    searchForm.value.asset_type = "";
    searchForm.value.sub_asset_type = "";
    searchForm.value.asset_type = "";
    searchForm.value.state = "";
    searchForm.value.city = city || "";
    searchForm.value.locality = "";
    searchForm.value.reserve_price = "";
    searchForm.value.institutes = "";
    searchForm.value.filter_by = "";
    searchForm.value.sort_by = "";
    searchForm.value.auction_start_date_unix_timestamp = "";
    searchForm.value.listing_id_query = "";
    searchForm.value.ab_verified = "";
  };

  const { reset: resetRequestingPropertyImagesStates, requestImages } =
    useRequestImages({
      withAuth,
      user,
      onSuccess: () => {
        setAcknowledgementOpen(true);
      },
      onError: () => {
        setAcknowledgementOpen(true);
        setAcknowledgmentErrorMessage(
          "Failed to request images. Please try again later.",
        );
      },
      propertyType: "auction_property",
      requestType: REQUEST_TYPES.AUCTION_PROPERTY_IMAGES,
    });

  const handleFiltersApply = () => {
    fetchFirstPage();
  };

  const handleRequestImagesClick = (property: PropertyDataInterface) => {
    if (!property?.listingId) return;
    requestImages(property?.listingId);
  };

  const handleSelectSortOption = (data: OptionType) => {
    if (data?.value === "all_properties") {
      handleClearFilters();
    }
    fetchFirstPage();
  };

  const handleClearFiltersHandler = () => {
    handleClearFilters();
    fetchFirstPage();
  };

  const sortByDropdownOptions = useMemo(
    () => [
      { label: "All Properties", value: "all_properties" },
      { label: "Auction date (Approaching)", value: "auction_date_asc" },
      { label: "Price: High to low", value: "reserve_price_desc" },
      { label: "Price: Low to high", value: "reserve_price_asc" },
    ],
    [],
  );

  return (
    <>
      <PropertyListPageWrapper
        isFilterOpen={isFilterOpen}
        cities={cities}
        footerChildren={
          <>
            {noMore && (
              <LazyComponent
                importFunc={() => import("app-v2").then((mod) => mod.Footer)}
              />
            )}

            <AuthModalWrapper
              userLoggedin={isLoggedIn}
              authModelOpen={authModalOpen}
              setAuthModelOpen={setAuthModalOpen}
            />
            <PDAcknowledgmentModal
              acknowledgementOpen={acknowledgementOpen}
              setAcknowledgementOpen={setAcknowledgementOpen}
              errorMessage={acknowledgmentErrorMessage}
              onClose={() => {
                resetRequestingPropertyImagesStates();
              }}
            />
          </>
        }
      >
        <PropertyListHeaderView
          isFilterOpen={isFilterOpen}
          title="Auction Properties"
          totalCount={searchForm?.value?.total}
          onSelectSortOption={handleSelectSortOption}
          onFilterToggle={() => setIsFilterOpen((v) => !v)}
          sortByDropdownOptions={sortByDropdownOptions}
          isLoading={loading}
        />

        <AuctionPropertyListPageSidebarFilterMenu
          filtersOpen={isFilterOpen}
          onClearFilters={handleClearFiltersHandler}
          onClose={() => setIsFilterOpen(false)}
          onFiltersApply={handleFiltersApply}
          setIsFilterOpen={setIsFilterOpen}
        />

        <div className="relative bg-white">
          <PropertyListViewWrapper
            allPropertiesData={data}
            isFetchingNextPage={loading}
            isFilterOpen={isFilterOpen}
            onRequestImagesClick={handleRequestImagesClick}
            propertyType="auction-properties"
          />
        </div>
      </PropertyListPageWrapper>
    </>
  );
};
